import "./SystemBanners.scss";
import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSocket } from "../../context/SocketContext";
import useAuthStore from "../../store/authStore";
import { getSystemBanners } from "../../api/systemBanners/getAll";
import { dismissSystemBanner } from "../../api/systemBanners/dismiss";
import { isBannerLive } from "../../utils/systemBanners";

// How often to check whether a scheduled banner should start or end
const CLOCK_INTERVAL = 30 * 1000;

// One banner (also used as the preview in the super admin)
export function SystemBanner({ banner, onDismiss }) {
  const { t } = useTranslation();

  return (
    <div
      className={`system-banner system-banner--${banner.color}`}
      role={banner.color === "critical" ? "alert" : "status"}
    >
      <span className="material-symbols-outlined system-banner__icon">
        {banner.icon}
      </span>
      <span className="system-banner__text">{banner.text}</span>
      {banner.dismissible && onDismiss && (
        <button
          className="system-banner__close"
          onClick={onDismiss}
          title={t("systemBanners.dismiss")}
          aria-label={t("systemBanners.dismiss")}
        >
          <span className="material-symbols-outlined">close</span>
        </button>
      )}
    </div>
  );
}

// The super admin's banners for everyone, stacked at the top of the app
function SystemBanners() {
  const token = useAuthStore((state) => state.token);
  const { socket } = useSocket();
  const [banners, setBanners] = useState([]);
  const [now, setNow] = useState(Date.now);

  const loadBanners = useCallback(async () => {
    if (!token) return;
    try {
      setBanners(await getSystemBanners());
    } catch (err) {
      console.error("Error fetching system banners:", err);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setBanners([]);
      return;
    }
    loadBanners();
  }, [token, loadBanners]);

  // Changed by the super admin, or dismissed on another device
  useEffect(() => {
    if (!socket) return;
    socket.on("systemBanners:changed", loadBanners);
    socket.on("connect", loadBanners);
    return () => {
      socket.off("systemBanners:changed", loadBanners);
      socket.off("connect", loadBanners);
    };
  }, [socket, loadBanners]);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), CLOCK_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  const handleDismiss = async (id) => {
    setBanners((prev) => prev.filter((banner) => banner._id !== id));
    try {
      await dismissSystemBanner(id);
    } catch (err) {
      console.error("Error dismissing system banner:", err);
      loadBanners();
    }
  };

  const liveBanners = banners.filter((banner) => isBannerLive(banner, now));
  if (liveBanners.length === 0) return null;

  return (
    <div className="system-banners">
      {liveBanners.map((banner) => (
        <SystemBanner
          key={banner._id}
          banner={banner}
          onDismiss={() => handleDismiss(banner._id)}
        />
      ))}
    </div>
  );
}

export default SystemBanners;
